import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import Swal from "sweetalert2";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../../Config/Config";

const EditDryerEquip = ({ editObj }) => {
  const [errors, setErrors] = useState([]);
  const [formData, setFormData] = useState({
    Id: editObj?.Id || 0,
    Name: editObj?.Name || "",
    EquipmentId: editObj?.EquipmentId || "",
    Min: editObj?.Min ?? "",
    Max: editObj?.max ?? "",
    Unit: editObj?.Unit ?? "",
    LeastCount: editObj?.LeastCount ?? "",
    OperatingMin: editObj?.Type === 3 ? null : editObj?.OperatingMin ?? "",
    OperatingMax: editObj?.Type === 3 ? null : editObj?.OperatingMax ?? "",
    UpdatedBy: JSON.parse(sessionStorage.getItem("user"))?.UserID || 0,
  });
  const [loading, setLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (editObj && editObj.Id) {
      const updatedFormData = {
        Id: editObj.Id,
        Name: editObj.Name || "",
        EquipmentId: editObj.EquipmentId || "",
        Min: editObj.Min ?? "",
        Max: editObj.max ?? "",
        Unit: editObj.Unit ?? "",
        LeastCount: editObj.LeastCount ?? "",
        OperatingMin:  editObj.OperatingMin ?? "",
        OperatingMax:  editObj.OperatingMax ?? "",
        UpdatedBy: JSON.parse(sessionStorage.getItem("user"))?.UserID || 0,
      };
      setFormData(updatedFormData);
      setErrors(validateForm({ ...updatedFormData, Type: editObj.Type }));
    }
  }, [editObj]);

  const logActivity = async (isSuccess, note) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const activityPayload = {
      ActivityType: 1,
      PerformedBy: user?.UserID,
      PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
      Notes: note,
      Location: "Equipment Update",
      IsSucces: isSuccess ? 1 : 0,
    };

    try {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityPayload),
      });
    } catch (err) {
      console.error("Failed to log activity:", err);
    }
  };

  const handleAuth = async () => {
    if (!password) {
      setAuthError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setAuthError("");

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await performUpdate();
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setAuthError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const performUpdate = async () => {
    const payload = {
      Id: formData.Id,
      Name: formData.Name,
      EquipmentId: formData.EquipmentId,
      Min:
        formData.Min !== "" && formData.Min !== null
          ? parseFloat(formData.Min)
          : null,
      Max:
        formData.Max !== "" && formData.Max !== null
          ? parseFloat(formData.Max)
          : null,
      Unit: formData.Unit !== "" ? formData.Unit : null,
      LeastCount: formData.LeastCount !== "" ? formData.LeastCount : null,
      OperatingMin:
       formData.OperatingMin !== "" && formData.OperatingMin !== null
          ? String(formData.OperatingMin)
          : null,
      OperatingMax:
       formData.OperatingMax !== "" && formData.OperatingMax !== null
          ? String(formData.OperatingMax)
          : null,
      UpdatedBy: formData.UpdatedBy,
    };

    let isSuccess = false;

    try {
      setLoading(true);

      const response = await fetch(
        `${getApiBaseUrl()}/api/updatePressureEquipmentsLite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      isSuccess = result?.ResultData[0]?.Status === "Success";

      if (isSuccess) {
        await logActivity(true, `Updated Equipment: ${formData.Name}`);
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: `${formData.Name} updated successfully!`,
        }).then(() => {
          window.location.reload();
        });
      } else {
        await logActivity(
          false,
          `Failed to update Equipment: ${formData.Name}`
        );
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: `Failed to update ${formData.Name}.`,
        });
      }
    } catch (error) {
      await logActivity(
        false,
        `Network error during equipment update: ${error.message}`
      );
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to send update request.",
      });
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setAuthError("");
    }
  };

  const validateForm = (data) => {
    let newErrors = [];

    if ("Type" in data && data.Type !== 3) {
      if (
        data.Min !== "" &&
        data.OperatingMin !== "" &&
        data.OperatingMin !== null &&
        parseFloat(data.OperatingMin) < parseFloat(data.Min)
      ) {
        newErrors.push(
          "Instrument Operating Min must be ≥ Instrument Min Range"
        );
      }

      if (
        data.Max !== "" &&
        data.OperatingMax !== "" &&
        data.OperatingMax !== null &&
        parseFloat(data.OperatingMax) > parseFloat(data.Max)
      ) {
        newErrors.push(
          "Instrument Operating Max must be ≤ Instrument Max Range"
        );
      }

      if (
        data.OperatingMin !== "" &&
        data.OperatingMax !== "" &&
        data.OperatingMin !== null &&
        data.OperatingMax !== null &&
        parseFloat(data.OperatingMin) > parseFloat(data.OperatingMax)
      ) {
        newErrors.push(
          "Instrument Operating Min must be ≤ Instrument Operating Max"
        );
      }
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedFormData = {
      ...formData,
      [name]: value,
    };

    setFormData(updatedFormData);
    setErrors(validateForm({ ...updatedFormData, Type: editObj.Type }));
    setAuthError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.EquipmentId) {
      setAuthError("Please fill in the Instrument ID field.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in the Instrument ID field.",
      });
      return;
    }

    if (errors.length > 0) {
      setAuthError("Please correct the form errors before submitting.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please correct the form errors before submitting.",
      });
      return;
    }

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (!user?.UserID) {
      setAuthError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true);
  };

  const clearEditData = () => {
    setErrors([]);
    // console.log("jhghjggk", formData);
    // setFormData({
    //   Id: formData?.Id,
    //   Name: formData?.Name || "",
    //   EquipmentId: formData?.EquipmentId || "",
    //   Min: formData?.Min ?? "",
    //   Max: formData?.max ?? "",
    //   Unit: formData?.Unit ?? "",
    //   LeastCount: formData?.LeastCount ?? "",
    //   OperatingMin: formData?.Type === 3 ? null : formData?.OperatingMin ?? "",
    //   OperatingMax: formData?.Type === 3 ? null : formData?.OperatingMax ?? "",
    //   UpdatedBy: JSON.parse(sessionStorage.getItem("user"))?.UserID || 0,
    // });
    setAskPassword(false);
    setPassword("");
    setAuthError("");
  };

  useEffect(() => {
    const offcanvas = document.getElementById("EditDryerEquipOffcanvas");
    if (offcanvas) {
      const handleClose = () => {
        clearEditData();
      };
      offcanvas.addEventListener("hidden.bs.offcanvas", handleClose);
      return () => {
        offcanvas.removeEventListener("hidden.bs.offcanvas", handleClose);
      };
    }
  }, []);

  return (
    <>
      <style>
        {`
        @media (max-width: 576px) {
          #EditDryerEquipOffcanvas {
            width: 30% !important;
          }
        }
        @media (min-width: 577px) and (max-width: 992px) {
          #EditDryerEquipOffcanvas {
            width: 35% !important;
          }
        }
        @media (min-width: 993px) and (max-width: 1440px) {
          #EditDryerEquipOffcanvas {
            width: 35% !important;
          }
        }
        @media (min-width: 1441px)  {
          #EditDryerEquipOffcanvas {
            width: 30% !important;
          }
        }
        `}
      </style>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="EditDryerEquipOffcanvas"
      >
        <form onSubmit={handleSubmit}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title me-4">Edit {editObj?.Name}</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
                disabled={errors.length > 0 || askPassword}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                disabled={askPassword}
              ></button>
            </div>
          </div>
          <div className="offcanvas-body">
            {authError && (
              <div className="alert alert-danger" role="alert">
                {authError}
              </div>
            )}
            <div className="row">
              <div className="mb-2 col-6 d-none">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  disabled
                />
              </div>
              <div className="mb-2 col-12">
                <label className="form-label">Instrument ID</label>
                <span className="text-danger ms-1">*</span>
                <input
                  type="text"
                  className="form-control"
                  name="EquipmentId"
                  value={formData.EquipmentId}
                  onChange={handleChange}
                  required
                  disabled={askPassword}
                />
              </div>
              <div className="mb-2 col-6">
                <label className="form-label">Instrument Min Range</label>
                <span className="text-danger ms-1">*</span>
                <input
                  type="number"
                  className="form-control"
                  name="Min"
                  value={formData.Min}
                  onChange={handleChange}
                  required
                  disabled={askPassword}
                />
              </div>
              <div className="mb-2 col-6">
                <label className="form-label">Instrument Max Range</label>
                <span className="text-danger ms-1">*</span>
                <input
                  type="number"
                  className="form-control"
                  name="Max"
                  value={formData.Max}
                  onChange={handleChange}
                  required
                  disabled={askPassword}
                />
              </div>
              <div className="mb-2 col-6">
                <label className="form-label">UOM</label>
                <input
                  type="text"
                  className="form-control"
                  name="Unit"
                  value={formData.Unit}
                  onChange={handleChange}
                  disabled={askPassword}
                />
              </div>
              <div className="mb-3 col-6">
                <label className="form-label">Least Count</label>
                <input
                  type="text"
                  className="form-control"
                  name="LeastCount"
                  value={formData.LeastCount}
                  onChange={handleChange}
                  disabled={askPassword}
                />
              </div>
             
                  <div className="mb-3 col-6">
                    <label className="form-label">
                      Instrument Operating Min
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="OperatingMin"
                      value={formData.OperatingMin}
                      onChange={handleChange}
                      disabled={askPassword}
                    />
                    {errors.some((err) => err.includes("Min")) && (
                      <small className="text-danger">
                        {errors.find((err) => err.includes("Min"))}
                      </small>
                    )}
                  </div>
                  <div className="mb-3 col-6">
                    <label className="form-label">
                      Instrument Operating Max
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="OperatingMax"
                      value={formData.OperatingMax}
                      onChange={handleChange}
                      disabled={askPassword}
                    />
                    {errors.some((err) => err.includes("Max")) && (
                      <small className="text-danger">
                        {errors.find((err) => err.includes("Max"))}
                      </small>
                    )}
                  </div>
               
        
              {askPassword && (
                <div className="mt-2">
                  <div className="mb-3 w-lg-75 m-auto">
                    <label className="form-label">
                      Confirm Password <span className="text-danger"> *</span>
                    </label>
                    <div className="pass-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="pass-input form-control"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setAuthError("");
                        }}
                        onCopy={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onCut={(e) => e.preventDefault()}
                        placeholder="Enter Password"
                      />
                      <span
                        className={`ti toggle-password ${
                          showPassword ? "ti-eye" : "ti-eye-off"
                        }`}
                        onClick={() => setShowPassword(!showPassword)}
                      ></span>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm me-2"
                    onClick={handleAuth}
                    disabled={authLoading || !password}
                  >
                    {authLoading ? "Verifying..." : "Confirm"}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setAskPassword(false);
                      setPassword("");
                      setAuthError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

EditDryerEquip.propTypes = {
  editObj: PropTypes.object,
};

export default EditDryerEquip;
